import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Chip,
} from '@mui/material';
import { Check, Close, Refresh, Info, ExpandMore, ExpandLess, PhoneIphone, Language, Android } from '@mui/icons-material';
import { AccessRequest, accessControlService } from '@watchlist/shared';
import InitialsAvatar from '../widgets/InitialsAvatar';
import { parseFirebaseDate, formatFirebaseDate } from '../../utils/dateUtils';

interface AccessRequestsTabProps {
  adminUid: string;
}

const PlatformIcon: React.FC<{ platform?: string }> = ({ platform }) => {
  if (platform === 'ios') return <PhoneIphone fontSize="small" />;
  if (platform === 'android') return <Android fontSize="small" />;
  return <Language fontSize="small" />;
};

const platformLabel = (platform?: string) => {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return 'Web';
};

const AccessRequestsTab: React.FC<AccessRequestsTabProps> = ({ adminUid }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState<AccessRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const pendingRequests = await accessControlService.getPendingRequests();
      setRequests(pendingRequests);
    } catch (err: any) {
      console.error('Error loading requests:', err);
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleApprove = async (request: AccessRequest) => {
    setProcessingId(request.id);
    try {
      await accessControlService.approveRequest(request.id, adminUid);
      setRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err: any) {
      console.error('Error approving request:', err);
      setError(err.message || 'Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (request: AccessRequest) => {
    setRejectingRequest(request);
    setRejectNote('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingRequest) return;

    setProcessingId(rejectingRequest.id);
    try {
      await accessControlService.rejectRequest(
        rejectingRequest.id,
        adminUid,
        rejectNote || undefined
      );
      setRequests(prev => prev.filter(r => r.id !== rejectingRequest.id));
      setRejectDialogOpen(false);
      setRejectingRequest(null);
    } catch (err: any) {
      console.error('Error rejecting request:', err);
      setError(err.message || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (timestamp: any) => {
    return formatFirebaseDate(timestamp, 'Unknown', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Pending Requests ({requests.length})
        </Typography>
        <IconButton onClick={loadRequests} size="small">
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            borderRadius: 2,
          }}
        >
          <Info sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
          <Typography color="text.secondary">No pending requests</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {requests.map(request => (
            <Box
              key={request.id}
              sx={{
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                overflow: 'hidden',
              }}
            >
              {/* Main row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                {/* Avatar */}
                {request.photoURL ? (
                  <Box
                    component="img"
                    src={request.photoURL}
                    alt=""
                    sx={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0 }}
                  />
                ) : (
                  <InitialsAvatar name={request.displayName} size={48} />
                )}

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 15,
                      color: theme.palette.text.primary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {request.displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: theme.palette.text.secondary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {request.email}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                    Requested {formatDate(request.requestedAt)}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {expandedId === request.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={
                      processingId === request.id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Check />
                      )
                    }
                    onClick={() => handleApprove(request)}
                    disabled={processingId === request.id}
                    sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<Close />}
                    onClick={() => handleRejectClick(request)}
                    disabled={processingId === request.id}
                    sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
                  >
                    Reject
                  </Button>
                </Box>
              </Box>

              {/* Expanded detail panel */}
              <Collapse in={expandedId === request.id}>
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <DetailRow label="Full name" value={request.displayName} />
                  <DetailRow label="Email" value={request.email} />
                  <DetailRow
                    label="Platform"
                    value={
                      <Chip
                        icon={<PlatformIcon platform={request.platform} />}
                        label={platformLabel(request.platform)}
                        size="small"
                        sx={{ height: 22, fontSize: 12 }}
                      />
                    }
                  />
                  <DetailRow label="Requested" value={formatDate(request.requestedAt)} />
                  <DetailRow label="User ID" value={request.uid} mono />
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      )}

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Access Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject the request from{' '}
            <strong>{rejectingRequest?.displayName}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection note (optional)"
            placeholder="This note will be shown to the user"
            value={rejectNote}
            onChange={e => setRejectNote(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={processingId === rejectingRequest?.id}
            sx={{ textTransform: 'none' }}
          >
            {processingId === rejectingRequest?.id ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, mono }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Typography
        sx={{
          fontSize: 12,
          color: theme.palette.text.disabled,
          minWidth: 80,
          flexShrink: 0,
          pt: '2px',
        }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography
          sx={{
            fontSize: 13,
            color: theme.palette.text.secondary,
            fontFamily: mono ? 'monospace' : undefined,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
};

export default AccessRequestsTab;
